import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { ExportDocumentStatus, ExportFacade, ExportListType, ExportService } from '@planone/bau365/data-access-export';
import { DataAccessScreensizeFacade } from '@planone/bau365/data-access-screensize';
import { DataAccessSearchFacade } from '@planone/bau365/data-access-search';
import { WatchlistFacade } from '@planone/bau365/data-access-user';
import { ModalBottomsheetHelper, ModalBottomsheetType } from '@planone/shared/ui-elements';
import { localizedMeta } from '@planone/shared/util-meta';
import { Observable, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'bau365-export-toggle-v2',
  templateUrl: './export-toggle-v2.component.html',
  styleUrl: './export-toggle-v2.component.scss',
})
export class ExportToggleV2Component implements OnInit, OnDestroy {
  @Input() listType?: ExportListType;
  @Input() emailSubject?: string;
  @Input() emailBody?: string;
  @Input() showForward = false;

  @Output()
  trackExportDialogOpened: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('exportTitle') public exportTitle: TemplateRef<never> | null | undefined;
  @ViewChild('exportBody') public exportBody: TemplateRef<never> | null | undefined;

  private size$: Observable<string | undefined>;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private size?: string;

  documentId$: Observable<string | undefined>;
  status$: Observable<ExportDocumentStatus>;
  defaultEmailSubject = localizedMeta['shareEmailSubject'];
  defaultEmailBody = localizedMeta['shareEmailBody'];
  documentId?: string;

  ExportDocumentStatus = ExportDocumentStatus;

  constructor(
    private modalBottomsheetHelper: ModalBottomsheetHelper,
    private exportFacade: ExportFacade,
    private watchlistFacade: WatchlistFacade,
    private searchFacade: DataAccessSearchFacade,
    public exportService: ExportService,
    screensizeFacade: DataAccessScreensizeFacade
  ) {
    this.documentId$ = exportFacade.documentId$;
    this.size$ = screensizeFacade.size$;
    this.status$ = exportFacade.status$;
  }

  ngOnInit(): void {
    this.size$.pipe(takeUntil(this.unsubscribe$)).subscribe((size?: string) => (this.size = size));
    this.documentId$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((documentId?: string) => (this.documentId = documentId));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  get exportTypeName() {
    let exportTypeName: string | undefined = undefined;

    if (this.listType === 'watchlist') {
      exportTypeName = $localize`:@@featureExport-watchlistFilename:Merkliste`;
    }

    if (this.listType === 'search') {
      exportTypeName = $localize`:@@featureExport-searchFilename:Entdecken`;
    }

    return exportTypeName;
  }

  toggle(): void {
    this.modalBottomsheetHelper.openDialog(
      ModalBottomsheetType.export,
      this.exportTitle,
      this.exportBody,
      this.size,
      undefined,
      {
        panelClass: 'export-dialog',
      },
      {
        panelClass: 'export-dialog',
      }
    );

    if (this.listType === 'watchlist') {
      this.watchlistFacade.generateWatchlistDocument();
    }

    if (this.listType === 'search') {
      this.searchFacade.generateSearchDocument();
    }

    this.exportFacade.methodSelectionOpened();

    this.trackExportDialogOpened.emit();
  }

  download(documentId: string): void {
    this.exportFacade.download({
      list: this.listType ?? 'download',
      exportTypeName: this.exportTypeName ?? 'unknown',
      to: 'download',
      documentId: documentId,
    });

    this.modalBottomsheetHelper.closeDialog();
  }

  track(type: string, documentId?: string): void {
    this.exportFacade.forward(type, documentId);
  }
}
